import { ConfigTab } from "../../types";
import { useEffect } from "react";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import { useNavigate, useParams } from "react-router-dom";

// Bi-directional mappings between tab names and indexes for consistent navigation
export const TAB_TO_INDEX = {
  [ConfigTab.MARKET_SIZING]: 0,
  [ConfigTab.WHITESPACE]: 1,
  [ConfigTab.COMPETITORS]: 2,
  [ConfigTab.SETTINGS]: 3,
};

const INDEX_TO_TAB = {
  0: ConfigTab.MARKET_SIZING,
  1: ConfigTab.WHITESPACE,
  2: ConfigTab.COMPETITORS,
  3: ConfigTab.SETTINGS,
};

/**
 * Custom hook to handle workspace configuration tab navigation and URL synchronization
 * @returns Object with navigation methods and current tab information
 */
export const useConfigTabNavigation = () => {
  const navigate = useNavigate();
  const workspaceId = useWorkspaceStore((state) => state.draft?.id);

  // Get method from store to sync tab state
  const setActiveConfigTab = useWorkspaceStore(
    (state) => state.setActiveConfigTab
  );

  // Local implementation of mapping functions (no need to use store)
  const getIndexFromTab = (tabName: string) =>
    TAB_TO_INDEX[tabName as ConfigTab] || 0;
  const getTabFromIndex = (index: number) =>
    INDEX_TO_TAB[index as keyof typeof INDEX_TO_TAB] || ConfigTab.MARKET_SIZING;

  // Get tab from URL or default to market-sizing
  const tabFromUrl =
    useParams<{ tab: string }>().tab || ConfigTab.MARKET_SIZING;

  // Get the active tab index using the local mapping method
  const activeTabIndex = getIndexFromTab(tabFromUrl);

  // Handle tab change and update URL
  const handleTabChange = (newValue: number) => {
    const newTab = getTabFromIndex(newValue);
    navigate(`/projects/${workspaceId}/configuration/${newTab}`);
  };

  // Get the next tab (for navigation)
  const getNextTab = () => {
    if (tabFromUrl === ConfigTab.MARKET_SIZING) {
      return ConfigTab.WHITESPACE;
    } else if (tabFromUrl === ConfigTab.WHITESPACE) {
      return ConfigTab.COMPETITORS;
    }
    return null; // No next tab if we're on the last one
  };

  // Get button text based on current tab
  const getNextButtonText = () => {
    if (tabFromUrl === ConfigTab.MARKET_SIZING)
      return "SAVE & CONFIGURE WHITESPACE";
    if (tabFromUrl === ConfigTab.WHITESPACE)
      return "SAVE & CONFIGURE COMPETITORS";
    return "SAVE";
  };

  // Sync WorkspaceStore's activeConfigTab with URL
  useEffect(() => {
    setActiveConfigTab(activeTabIndex);
  }, [activeTabIndex, setActiveConfigTab]);

  return {
    activeTabIndex,
    currentTab: tabFromUrl,
    handleTabChange,
    getNextTab,
    getNextButtonText,
  };
};
